import Favorite from "@mui/icons-material/Favorite";
import FavoriteBorder from "@mui/icons-material/FavoriteBorder";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import GridViewRounded from "@mui/icons-material/GridViewRounded";
import ViewListRounded from "@mui/icons-material/ViewListRounded";
import BlotterFilterBar, {
  type AppliedFilter,
  type FilterField,
} from "@/components/BlotterFilterBar";
import {
  TY_CHIP_SUBMITTED_PREFIX,
  TY_EXPORT_LABEL,
  TY_EXPORTING_LABEL,
  TY_FAVORITES_LABEL,
  TY_FILTER_CAMPAIGN_LABEL,
  TY_FILTER_FIELD_IDS,
  TY_FILTER_SUBMITTED_LABEL,
  TY_SEARCH_PLACEHOLDER,
} from "@/constants/thankYouNotes";
import type { ThankYouNotesView } from "@/interfaces/thankYouNotes";
import { submittedChipLabel } from "@/utils/thankYouNotes-helper";

interface ThankYouNotesControlsProps {
  search: string;
  onSearchChange: (v: string) => void;
  filters: AppliedFilter[];
  onFiltersChange: (f: AppliedFilter[]) => void;
  campaignOptions: string[];
  favOnly: boolean;
  onToggleFavOnly: () => void;
  view: ThankYouNotesView;
  onViewChange: (v: ThankYouNotesView) => void;
  onExport: () => void;
  /** The export walks every page, so it is slow enough to double-click through. */
  isExporting: boolean;
}

const VIEW_BUTTON_BASE =
  "inline-flex items-center justify-center h-full border-none py-[7px] px-[10px] cursor-pointer";

function viewButtonClass(isActive: boolean, withDivider: boolean): string {
  const divider = withDivider ? " border-l border-smoke" : "";
  const tone = isActive ? "bg-dark-dust text-white" : "bg-white text-graphite";
  return `${VIEW_BUTTON_BASE}${divider} ${tone}`;
}

/**
 * Thank You Notes controls row (GFG-233): the canonical sf-bar (search + campaign / submitted
 * filters) on the left, then Favorites, the grid/list toggle and Export CSV on the right.
 * Purely presentational — every piece of state and the export itself belong to the page.
 */
export default function ThankYouNotesControls({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  campaignOptions,
  favOnly,
  onToggleFavOnly,
  view,
  onViewChange,
  onExport,
  isExporting,
}: ThankYouNotesControlsProps) {
  const fields: FilterField[] = [
    {
      id: TY_FILTER_FIELD_IDS.campaign,
      label: TY_FILTER_CAMPAIGN_LABEL,
      options: campaignOptions.map((c) => ({ value: c, label: c })),
    },
    {
      id: TY_FILTER_FIELD_IDS.submitted,
      label: TY_FILTER_SUBMITTED_LABEL,
      // `daterange` ignores `options`, but the field requires the key.
      options: [],
      kind: "daterange",
      chipLabel: (values) =>
        `${TY_CHIP_SUBMITTED_PREFIX}: ${submittedChipLabel(values[0] || null, values[1] || null)}`,
    },
  ];

  /**
   * Campaign is single-select (the chip reads "Campaign: New Hire Welcome"), but BlotterFilterBar
   * is multi-select by nature and accumulates values — keeping only the last one makes a second
   * pick replace the first, while unpicking the current one still empties the list and lets the
   * bar drop the filter entirely.
   */
  function handleFiltersChange(next: AppliedFilter[]) {
    onFiltersChange(
      next.map((f) =>
        f.fieldId === TY_FILTER_FIELD_IDS.campaign ? { ...f, values: f.values.slice(-1) } : f,
      ),
    );
  }

  return (
    <div className="flex items-center gap-3 mb-4 flex-wrap">
      <BlotterFilterBar
        placeholder={TY_SEARCH_PLACEHOLDER}
        search={search}
        onSearchChange={onSearchChange}
        fields={fields}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <div className="flex items-center gap-[10px] shrink-0">
        <button
          type="button"
          aria-pressed={favOnly}
          onClick={onToggleFavOnly}
          className={`inline-flex items-center gap-[6px] h-10 box-border border rounded-lg py-[7px] px-[12px] font-lato text-[13px] font-bold cursor-pointer transition-colors ${
            favOnly
              ? "border-dark-dust bg-wax text-rodeo-black"
              : "border-smoke bg-white text-graphite hover:border-dark-dust"
          }`}
        >
          {favOnly ? (
            <Favorite className="text-dark-dust" style={{ fontSize: "17px" }} />
          ) : (
            <FavoriteBorder className="text-rodeo-dust" style={{ fontSize: "17px" }} />
          )}
          {TY_FAVORITES_LABEL}
        </button>

        <div className="inline-flex h-10 box-border border border-smoke rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => onViewChange("grid")}
            className={viewButtonClass(view === "grid", false)}
            aria-label="Card view"
            aria-pressed={view === "grid"}
          >
            <GridViewRounded style={{ fontSize: "18px" }} />
          </button>
          <button
            type="button"
            onClick={() => onViewChange("list")}
            className={viewButtonClass(view === "list", true)}
            aria-label="List view"
            aria-pressed={view === "list"}
          >
            <ViewListRounded style={{ fontSize: "18px" }} />
          </button>
        </div>

        <button
          type="button"
          onClick={onExport}
          disabled={isExporting}
          className="inline-flex items-center gap-2 h-10 box-border bg-white text-rodeo-black border border-dark-dust py-[11px] px-[20px] rounded-[6px] font-lato text-[11px] font-bold uppercase tracking-[1.5px] cursor-pointer transition-colors hover:bg-wax whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <FileDownloadOutlined style={{ fontSize: "18px" }} />{" "}
          {isExporting ? TY_EXPORTING_LABEL : TY_EXPORT_LABEL}
        </button>
      </div>
    </div>
  );
}
